import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bot,
  CheckCircle,
  Clock,
  MessageSquare,
  XCircle,
  Send,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { showToastError } from "../../shared/alertService";
import { cn } from "../../utils/cn";
import { getPlatformConfig, PlatformIcon } from "../../utils/platformIcons";

export interface ConversationMessage {
  text: string;
  sender: string;
  is_user: boolean;
  created_at: string;
}

export interface AutoResponse {
  id: string;
  trigger: string;
  response: string;
  confidence: number;
  status: "pending" | "approved" | "rejected" | "sent" | "responded";
  platform: string;
  user_id: string;
  comment_text?: string;
  created_at: string;
  approved_at?: string;
  sent_at?: string;
  sentiment?: "positive" | "negative" | "neutral";
  sentiment_score?: number;
  message_type?: string;
  source_channel?: string;
  source_detail?: string;
  custom_tags?: number[];
  organization_id?: number | null;
  client_company_id?: number | null;
  lead_id?: string | null;
  metadata?: {
    conversation_history?: ConversationMessage[];
    [key: string]: any;
  };
}

export interface CustomTagDef {
  id: number;
  name: string;
  color: string;
}

interface Column {
  id: string;
  label: string;
  color: string;
  borderColor: string;
  bgColor: string;
  textColor: string;
  icon: React.FC<{
    size?: number;
    className?: string;
    style?: React.CSSProperties;
    color?: string;
  }>;
  readonly?: boolean;
}

const getColumns = (t: (key: string) => string): Column[] => [
  {
    id: "pending",
    label: t("Pendente"),
    color: "#f59e0b",
    borderColor: "border-amber-200",
    bgColor: "bg-amber-50",
    textColor: "text-amber-600",
    icon: Clock,
  },
  {
    id: "approved",
    label: t("Aprovar"),
    color: "#10b981",
    borderColor: "border-emerald-200",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-600",
    icon: CheckCircle,
  },
  {
    id: "send_action",
    label: t("Enviar Agora"),
    color: "#3b82f6",
    borderColor: "border-blue-200",
    bgColor: "bg-blue-50",
    textColor: "text-blue-600",
    icon: Send,
  },
  {
    id: "rejected",
    label: t("Rejeitar"),
    color: "#ef4444",
    borderColor: "border-red-200",
    bgColor: "bg-red-50",
    textColor: "text-red-600",
    icon: XCircle,
  },
  {
    id: "sent",
    label: t("Enviado"),
    color: "#8b5cf6",
    borderColor: "border-purple-200",
    bgColor: "bg-purple-50",
    textColor: "text-purple-600",
    icon: MessageSquare,
  },
];

const SENTIMENT_STYLES = {
  positive: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-200",
    emoji: "😊",
  },
  negative: {
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-200",
    emoji: "😟",
  },
  neutral: {
    bg: "bg-gray-50",
    text: "text-gray-500",
    border: "border-gray-200",
    emoji: "😐",
  },
};

const getMessageTypeLabels = (
  t: (key: string) => string,
): Record<string, string> => ({
  question: t("Pergunta"),
  complaint: t("Reclamação"),
  compliment: t("Elogio"),
  spam: "Spam",
  lead: "Lead",
  other: t("Outro"),
});

// ─── Card Component ────────────────────────────────────────────────────────────

interface KanbanCardProps {
  response: AutoResponse;
  isDragging?: boolean;
  onClick: (r: AutoResponse) => void;
  customTags?: CustomTagDef[];
}

const KanbanCard: React.FC<KanbanCardProps> = ({
  response,
  isDragging,
  onClick,
  customTags = [],
}) => {
  const { t } = useTranslation();
  const messageTypeLabels = getMessageTypeLabels(t);
  const sentiment = response.sentiment
    ? SENTIMENT_STYLES[response.sentiment]
    : null;
  const msgTypeLabel = response.message_type
    ? messageTypeLabels[response.message_type] || response.message_type
    : null;
  const displayPlatform = response.source_detail
    ?.toLowerCase()
    .includes("instagram")
    ? "instagram"
    : response.platform;

  return (
    <div
      onClick={() => onClick(response)}
      className={cn(
        "bg-white rounded-2xl border border-purple-200 p-4 cursor-pointer select-none",
        "hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-200",
        isDragging &&
          "opacity-50 scale-95 rotate-1 shadow-2xl shadow-purple-500/20",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "w-8 h-8 rounded-xl border flex items-center justify-center shrink-0",
              getPlatformConfig(displayPlatform).bgColor,
              getPlatformConfig(displayPlatform).color,
              getPlatformConfig(displayPlatform).borderColor,
            )}
          >
            <PlatformIcon platform={displayPlatform} size={16} />
          </div>
          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            {displayPlatform}
          </span>
        </div>
        <span className="text-[10px] font-bold text-gray-400">
          {response.confidence != null
            ? `${(response.confidence * 100).toFixed(0)}%`
            : ""}
        </span>
      </div>

      {/* Trigger */}
      <p className="text-[12px] text-gray-700 font-medium italic line-clamp-2 border-l-2 border-purple-200 pl-2 mb-2">
        "{response.trigger}"
      </p>

      {/* Response preview */}
      <p className="text-[11px] text-purple-800 font-semibold line-clamp-1 mb-3">
        {response.response}
      </p>

      {/* Tags */}
      {(sentiment ||
        msgTypeLabel ||
        response.source_detail ||
        (response.custom_tags || []).length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-purple-50">
          {sentiment && (
            <span
              className={cn(
                "text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border",
                sentiment.bg,
                sentiment.text,
                sentiment.border,
              )}
            >
              {sentiment.emoji} {response.sentiment}
            </span>
          )}
          {msgTypeLabel && (
            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border bg-purple-50 text-purple-600 border-purple-200">
              {msgTypeLabel}
            </span>
          )}
          {(response.custom_tags || []).map((tagId) => {
            const tag = customTags.find((ct) => ct.id === tagId);
            if (!tag) return null;
            return (
              <span
                key={tag.id}
                className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
                style={{
                  background: `${tag.color}20`,
                  borderColor: `${tag.color}40`,
                  color: tag.color,
                }}
              >
                {tag.name}
              </span>
            );
          })}
          {response.source_detail && (
            <span className="text-[9px] text-gray-400 font-medium ml-auto truncate max-w-[100px]">
              {response.source_detail}
            </span>
          )}
        </div>
      )}

      {/* Date */}
      <p className="text-[9px] text-gray-400 mt-2 font-medium">
        {response.created_at
          ? new Date(response.created_at).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : ""}
      </p>
    </div>
  );
};

// ─── Sortable Card Wrapper ─────────────────────────────────────────────────────

const SortableCard: React.FC<KanbanCardProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.response.id,
    data: { status: props.response.status },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <KanbanCard {...props} isDragging={isDragging} />
    </div>
  );
};

// ─── Column Component ──────────────────────────────────────────────────────────

interface KanbanColumnProps {
  column: Column;
  responses: AutoResponse[];
  count: number;
  onCardClick: (r: AutoResponse) => void;
  isOver?: boolean;
  customTags?: CustomTagDef[];
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  responses,
  count,
  onCardClick,
  isOver: externalIsOver,
  customTags,
}) => {
  const { t } = useTranslation();
  const ColIcon = column.icon;
  const { setNodeRef, isOver: droppableIsOver } = useDroppable({
    id: column.id,
    disabled: column.readonly,
  });
  const highlighted = droppableIsOver || externalIsOver;

  return (
    <div className="flex flex-col min-w-[240px] flex-1 max-w-[310px]">
      {/* Column Header */}
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3 rounded-2xl border mb-3 transition-all duration-200",
          column.bgColor,
          column.borderColor,
          highlighted && !column.readonly && "ring-2 shadow-md scale-[1.02]",
        )}
        style={
          highlighted && !column.readonly
            ? ({ "--tw-ring-color": column.color } as React.CSSProperties)
            : {}
        }
      >
        <div className="flex items-center gap-2">
          <ColIcon size={14} className={column.textColor} />
          <span
            className={cn(
              "text-[11px] font-black uppercase tracking-widest",
              column.textColor,
            )}
          >
            {column.label}
          </span>
        </div>
        <span
          className="text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border"
          style={{
            background: `${column.color}20`,
            borderColor: `${column.color}40`,
            color: column.color,
          }}
        >
          {count}
        </span>
      </div>

      {/* Drop Zone */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex-1 rounded-2xl border-2 border-dashed p-2 min-h-[160px] flex flex-col gap-2 transition-all duration-200",
          column.borderColor,
          column.readonly && "opacity-60",
        )}
        style={
          highlighted && !column.readonly
            ? {
                borderStyle: "solid",
                borderColor: column.color,
                background: `${column.color}0D`,
              }
            : {}
        }
      >
        <SortableContext
          items={responses.map((r) => r.id)}
          strategy={verticalListSortingStrategy}
        >
          {responses.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 text-center gap-2">
              {highlighted && !column.readonly ? (
                <>
                  <ColIcon size={28} style={{ color: column.color }} />
                  <p
                    className="text-[11px] font-black uppercase tracking-widest"
                    style={{ color: column.color }}
                  >
                    {t("Solte para {label}", {
                      label: column.label.toLowerCase(),
                    })}
                  </p>
                </>
              ) : (
                <>
                  <Bot size={24} className="text-gray-300" />
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                    {column.readonly ? t("Nenhuma") : t("Arraste aqui")}
                  </p>
                </>
              )}
            </div>
          ) : (
            responses.map((r) => (
              <SortableCard
                key={r.id}
                response={r}
                onClick={onCardClick}
                customTags={customTags}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
};

// ─── Main Kanban Board ─────────────────────────────────────────────────────────

interface AutoResponderKanbanProps {
  responses: AutoResponse[];
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onSend?: (id: string, response?: AutoResponse) => Promise<void>;
  onCardClick: (response: AutoResponse) => void;
  onDataChange: () => void;
  customTags?: CustomTagDef[];
}

const AutoResponderKanban: React.FC<AutoResponderKanbanProps> = ({
  responses,
  onApprove,
  onReject,
  onSend,
  onCardClick,
  onDataChange,
  customTags,
}) => {
  const { t } = useTranslation();
  const columns = useMemo(() => getColumns(t), [t]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColId, setOverColId] = useState<string | null>(null);
  const [localResponses, setLocalResponses] =
    useState<AutoResponse[]>(responses);

  React.useEffect(() => {
    setLocalResponses(responses);
  }, [responses]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const byColumn = useMemo(() => {
    const map: Record<string, AutoResponse[]> = {};
    columns.forEach((c) => {
      map[c.id] = [];
    });
    localResponses.forEach((r) => {
      const col = r.status === "responded" ? "sent" : r.status;
      if (map[col]) map[col].push(r);
    });
    return map;
  }, [localResponses]);

  const activeResponse = useMemo(
    () => localResponses.find((r) => r.id === activeId) ?? null,
    [localResponses, activeId],
  );

  const getColumnForCard = (id: string): string | null => {
    for (const [colId, cards] of Object.entries(byColumn)) {
      if (cards.find((c) => c.id === id)) return colId;
    }
    return null;
  };

  const resolveTargetCol = (overId: string): string | null =>
    columns.find((c) => c.id === overId)?.id ?? getColumnForCard(overId);

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const handleDragOver = (e: DragOverEvent) => {
    setOverColId(e.over ? resolveTargetCol(String(e.over.id)) : null);
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    setOverColId(null);
    const { active, over } = e;
    if (!over) return;

    const sourceCol = getColumnForCard(String(active.id));
    const targetCol = resolveTargetCol(String(over.id));

    if (!sourceCol || !targetCol || sourceCol === targetCol) return;

    const targetColumn = columns.find((c) => c.id === targetCol);
    if (targetColumn?.readonly) return;

    const responseId = String(active.id);

    if (targetCol !== "send_action") {
      setLocalResponses((prev) =>
        prev.map((r) =>
          r.id === responseId
            ? { ...r, status: targetCol as AutoResponse["status"] }
            : r,
        ),
      );
    }

    try {
      if (targetCol === "approved") {
        await onApprove(responseId);
      } else if (targetCol === "rejected") {
        await onReject(responseId);
      } else if (targetCol === "send_action" || targetCol === "sent") {
        if (onSend) {
          const resp = localResponses.find((r) => r.id === responseId);
          await onSend(responseId, resp);
        } else {
          await onApprove(responseId);
        }
      } else {
        setLocalResponses(responses);
        return;
      }
      onDataChange();
    } catch (err: any) {
      setLocalResponses(responses);
      showToastError(
        err?.message ||
          t("Não foi possível mover a resposta. Tente novamente."),
      );
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 overflow-x-auto pb-4">
        {columns.map((col) => (
          <KanbanColumn
            key={col.id}
            column={col}
            responses={byColumn[col.id] ?? []}
            count={byColumn[col.id]?.length ?? 0}
            onCardClick={onCardClick}
            isOver={overColId === col.id}
            customTags={customTags}
          />
        ))}
      </div>

      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
      >
        {activeResponse ? (
          <KanbanCard
            response={activeResponse}
            isDragging={false}
            onClick={() => {}}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default AutoResponderKanban;
